import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, Form, Button, Alert } from 'react-bootstrap'
import axios from 'axios'
import { CSVLink } from 'react-csv'
import FilePicker from 'sharedComponents/SearchBox/FilePicker'
import { HiChevronUpDown } from 'react-icons/hi2'
import { getAxiosDefaultConfig } from 'utils'
import { importEbayOrdersUrl } from 'urls'
import { selectRootAccounts, selectRootUser } from '../../../rootSlice'
import { postSlackMessage } from '../integrationsSlice'
import { SLACK_DATA_STATUS_CHANNEL_ID } from '../../../modules/constants'
import { fetchBackfill } from '../../../actions/backFillActions'

// Columns match eBay Seller Hub's Orders Report export. Template is just a
// header skeleton — eBay's real export has a metadata banner above row 1 and a
// summary block below the data; papaya's parser strips both.
const EBAY_TEMPLATE_HEADERS = [
  { label: 'Sales Record Number', key: 'Sales Record Number' },
  { label: 'Order Number', key: 'Order Number' },
  { label: 'Buyer Username', key: 'Buyer Username' },
  { label: 'Buyer Name', key: 'Buyer Name' },
  { label: 'Buyer Email', key: 'Buyer Email' },
  { label: 'Buyer Phone', key: 'Buyer Phone' },
  { label: 'Ship To Name', key: 'Ship To Name' },
  { label: 'Ship To Phone', key: 'Ship To Phone' },
  { label: 'Ship To Address 1', key: 'Ship To Address 1' },
  { label: 'Ship To Address 2', key: 'Ship To Address 2' },
  { label: 'Ship To City', key: 'Ship To City' },
  { label: 'Ship To State', key: 'Ship To State' },
  { label: 'Ship To Zip', key: 'Ship To Zip' },
  { label: 'Ship To Country', key: 'Ship To Country' },
  { label: 'Item Number', key: 'Item Number' },
  { label: 'Item Title', key: 'Item Title' },
  { label: 'Custom Label', key: 'Custom Label' },
  { label: 'Variation Details', key: 'Variation Details' },
  { label: 'Quantity', key: 'Quantity' },
  { label: 'Sold For', key: 'Sold For' },
  { label: 'Shipping And Handling', key: 'Shipping And Handling' },
  { label: 'eBay Collected Tax', key: 'eBay Collected Tax' },
  { label: 'Seller Collected Tax', key: 'Seller Collected Tax' },
  { label: 'Total Price', key: 'Total Price' },
  { label: 'Payment Method', key: 'Payment Method' },
  { label: 'Sale Date', key: 'Sale Date' },
  { label: 'Paid On Date', key: 'Paid On Date' },
  { label: 'Ship By Date', key: 'Ship By Date' },
  { label: 'Shipped On Date', key: 'Shipped On Date' },
  { label: 'Shipping Service', key: 'Shipping Service' },
  { label: 'Tracking Number', key: 'Tracking Number' },
  { label: 'Carrier Used', key: 'Carrier Used' },
  { label: 'Feedback Left', key: 'Feedback Left' },
  { label: 'Private Notes', key: 'Private Notes' },
]

const EBAY_TEMPLATE_SAMPLE = [
  {
    'Sales Record Number': '1001',
    'Order Number': '12-13456-78901',
    'Buyer Username': 'sample_buyer',
    'Buyer Name': 'Jane Doe',
    'Buyer Email': 'j***e@members.ebay.com',
    'Buyer Phone': '',
    'Ship To Name': 'Jane Doe',
    'Ship To Phone': '',
    'Ship To Address 1': '123 Main St',
    'Ship To Address 2': '',
    'Ship To City': 'Seattle',
    'Ship To State': 'WA',
    'Ship To Zip': '98101',
    'Ship To Country': 'United States',
    'Item Number': '285943120877',
    'Item Title': 'Sample Item',
    'Custom Label': 'SKU-001',
    'Variation Details': '',
    'Quantity': '1',
    'Sold For': '19.99',
    'Shipping And Handling': '5.00',
    'eBay Collected Tax': '1.68',
    'Seller Collected Tax': '0.00',
    'Total Price': '26.67',
    'Payment Method': 'eBay',
    'Sale Date': 'Apr-02-26 14:22:18 PDT',
    'Paid On Date': 'Apr-02-26 14:24:01 PDT',
    'Ship By Date': 'Apr-07-26',
    'Shipped On Date': 'Apr-03-26 11:15:00 PDT',
    'Shipping Service': 'USPS Ground Advantage',
    'Tracking Number': '9400111899561234567890',
    'Carrier Used': 'USPS',
    'Feedback Left': '',
    'Private Notes': '',
  },
]

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10 MB

type SourceKind = 'csv_upload' | 'google_sheet_picker' | 'google_sheet_url'

interface UploadResult {
  file_id: number
  total_rows: number
  total_orders: number
}

function ebayAccountLabel(account: any, index: number): string {
  // Prefer the eBay seller ID (the user-facing handle, e.g. "evalueshopper").
  // It matches what the eBay integration card already shows under "ID".
  return account.merchant_id || account.account_name || account.account_email || account.email || `Account ${index + 1}`
}

function driveAccountLabel(account: any, index: number): string {
  return account.email || account.account_name || account.merchant_id || `Drive account ${index + 1}`
}

export default function ImportOrdersModal({ visible, closeModal }: { visible: boolean; closeModal: () => void }) {
  const dispatch = useDispatch()
  const user = useSelector(selectRootUser)
  const accounts = useSelector(selectRootAccounts)
  const ebayAccounts = accounts.filter((a: any) => a.channel === 'eBay')
  const driveAccounts = accounts.filter((a: any) => a.channel === 'Google Drive')
  const googleDriveLinked = driveAccounts.length > 0

  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [pickedSheet, setPickedSheet] = useState<{ id: string; name: string; accessToken?: string } | null>(null)
  const [sheetUrl, setSheetUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [missingColumns, setMissingColumns] = useState<string[] | null>(null)
  const [result, setResult] = useState<UploadResult | null>(null)
  const [backfillError, setBackfillError] = useState<string | null>(null)
  const [startingBackfill, setStartingBackfill] = useState(false)
  const [selectedEbayIndex, setSelectedEbayIndex] = useState(0)
  const [selectedDriveIndex, setSelectedDriveIndex] = useState(0)

  useEffect(() => {
    if (!visible) {
      setSelectedEbayIndex(0)
      setSelectedDriveIndex(0)
    }
  }, [visible])
  useEffect(() => {
    if (selectedEbayIndex >= ebayAccounts.length) setSelectedEbayIndex(0)
  }, [ebayAccounts.length, selectedEbayIndex])
  useEffect(() => {
    if (selectedDriveIndex >= driveAccounts.length) setSelectedDriveIndex(0)
    setPickedSheet(null)
  }, [selectedDriveIndex, driveAccounts.length])

  const selectedDriveAccount = driveAccounts[selectedDriveIndex] || driveAccounts[0]

  const selectedAccount = ebayAccounts[selectedEbayIndex] || ebayAccounts[0]
  const selectedMerchantId = selectedAccount?.merchant_id

  const reset = () => {
    setCsvFile(null)
    setPickedSheet(null)
    setSheetUrl('')
    setLoading(false)
    setError(null)
    setMissingColumns(null)
    setResult(null)
    setBackfillError(null)
    setStartingBackfill(false)
  }

  const handleClose = () => {
    reset()
    closeModal()
  }

  const submitUpload = async (source: SourceKind, payload: any) => {
    if (!selectedMerchantId) {
      setError('Pick an eBay account first.')
      return
    }
    setError(null)
    setMissingColumns(null)
    setLoading(true)
    try {
      const { data } = await axios.post(
        importEbayOrdersUrl,
        { source, merchant_id: selectedMerchantId, ...payload },
        getAxiosDefaultConfig(),
      )
      setResult(data as UploadResult)
      dispatch(
        postSlackMessage({
          channel: SLACK_DATA_STATUS_CHANNEL_ID,
          text: `*eBay CSV import* by ${user?.email}: ${data.total_orders} orders / ${data.total_rows} rows (source=${source})`,
        }) as any,
      )
    } catch (err: any) {
      const data = err?.response?.data
      if (data?.missing) {
        setMissingColumns(data.missing)
        setError(`Your file is missing required columns.`)
      } else {
        setError(data?.error || 'Something went wrong processing the import.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCsvUpload = () => {
    if (!csvFile) {
      setError('Please pick a CSV file first.')
      return
    }
    if (csvFile.size > MAX_FILE_SIZE_BYTES) {
      setError(`File is too large (${(csvFile.size / 1024 / 1024).toFixed(1)} MB). Limit is 10 MB.`)
      return
    }
    const reader = new FileReader()
    reader.onerror = () => setError('Failed to read file.')
    reader.onload = () => {
      const csvText = typeof reader.result === 'string' ? reader.result : ''
      if (!csvText) {
        setError('File appears to be empty.')
        return
      }
      submitUpload('csv_upload', { csvText, filename: csvFile.name })
    }
    reader.readAsText(csvFile)
  }

  const handleSheetsPickerUpload = () => {
    if (!pickedSheet) {
      setError('Please pick a Google Sheet first.')
      return
    }
    if (!selectedDriveAccount?.merchant_id) {
      setError('No Google Drive account selected.')
      return
    }
    submitUpload('google_sheet_picker', {
      sheetsId: pickedSheet.id,
      drive_merchant_id: selectedDriveAccount.merchant_id,
      picker_access_token: pickedSheet.accessToken,
      filename: pickedSheet.name,
    })
  }

  const handleSheetsUrlUpload = () => {
    if (!sheetUrl.trim()) {
      setError('Please paste a Google Sheets URL.')
      return
    }
    submitUpload('google_sheet_url', { url: sheetUrl.trim() })
  }

  const startBackfill = async () => {
    if (!result || !selectedMerchantId) return
    setBackfillError(null)
    setStartingBackfill(true)
    try {
      const outcome: any = await dispatch(
        fetchBackfill(selectedMerchantId, 'eBay', { csv_job_id: result.file_id, total_orders: result.total_orders }) as any,
      )
      if (outcome && outcome.ok === false) {
        const prefix = outcome.status ? `${outcome.status} — ` : ''
        setBackfillError(`${prefix}${outcome.message || 'Failed to start backfill'}`)
        return
      }
      handleClose()
    } finally {
      setStartingBackfill(false)
    }
  }

  if (ebayAccounts.length === 0) {
    return (
      <Modal show={visible} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Import historical eBay orders</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            Connect an eBay account first to import historical orders.
          </Alert>
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <Modal show={visible} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Import historical eBay orders</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result ? (
          <div>
            <Alert variant="success">
              Parsed <strong>{result.total_orders}</strong> orders across{' '}
              <strong>{result.total_rows}</strong> rows.
            </Alert>
            <p className="tw-text-sm tw-text-slate-600">
              Click <strong>Start Backfill</strong> to send these orders to PayHelm. Live-synced
              orders (within eBay's 90-day window) take priority — duplicates from this CSV will be
              skipped.
            </p>
            {backfillError && (
              <Alert variant="danger" className="tw-mt-3">
                Could not start backfill: {backfillError}
              </Alert>
            )}
          </div>
        ) : (
          <>
            <p className="tw-text-sm tw-text-slate-600 tw-mb-3">
              Export your orders from eBay Seller Hub (Orders → Download report), then upload here.
              For very long history, splitting into multiple files helps.
            </p>

            {ebayAccounts.length > 1 && (
              <div className="tw-mb-3">
                <div className="tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1">eBay account</div>
                <div className="tw-relative tw-w-full">
                  <select
                    className="myselect tw-bg-gray-100 tw-border-transparent tw-cursor-pointer tw-w-full tw-rounded-md tw-border tw-py-2 tw-px-3 tw-pr-8"
                    value={String(selectedEbayIndex)}
                    onChange={(e) => setSelectedEbayIndex(parseInt(e.target.value, 10))}
                    disabled={loading}
                  >
                    {ebayAccounts.map((a: any, i: number) => (
                      <option key={a.merchant_id || i} value={String(i)}>
                        {ebayAccountLabel(a, i)}
                      </option>
                    ))}
                  </select>
                  <div className="tw-absolute tw-top-2 tw-right-1.5 tw-text-slate-400 tw-text-xl tw-pointer-events-none">
                    <HiChevronUpDown />
                  </div>
                </div>
                <div className="form-text tw-text-slate-500 tw-text-sm tw-mt-1">
                  Imported orders will be attributed to this account.
                </div>
              </div>
            )}

            <div className="tw-flex tw-justify-end tw-mb-2">
              <CSVLink
                data={EBAY_TEMPLATE_SAMPLE}
                headers={EBAY_TEMPLATE_HEADERS}
                filename="ebay-import-template.csv"
              >
                <Button variant="outline-secondary" size="sm">
                  Download template
                </Button>
              </CSVLink>
            </div>

            {error && (
              <Alert variant="danger">
                {error}
                {missingColumns && missingColumns.length > 0 && (
                  <ul className="tw-mt-2 tw-mb-0">
                    {missingColumns.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                )}
              </Alert>
            )}

            <Form.Group className="tw-mb-3">
              <Form.Label>Upload CSV</Form.Label>
              <Form.Control
                type="file"
                accept=".csv"
                size="sm"
                disabled={loading}
                onChange={(e: any) => setCsvFile(e.target.files?.[0] || null)}
              />
              {csvFile && (
                <div className="tw-mt-2">
                  <Button size="sm" onClick={handleCsvUpload} disabled={loading}>
                    {loading ? 'Uploading…' : `Upload ${csvFile.name}`}
                  </Button>
                </div>
              )}
            </Form.Group>

            <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-full tw-my-3">
              <hr className="tw-flex-1" />
              <span className="tw-mx-3 tw-text-slate-500 tw-text-sm">or</span>
              <hr className="tw-flex-1" />
            </div>

            <Form.Group className="tw-mb-3">
              <Form.Label>From Google Sheet (picker)</Form.Label>
              {driveAccounts.length > 1 && (
                <div className="tw-mb-2">
                  <div className="tw-text-xs tw-text-slate-600 tw-mb-1">Google Drive account</div>
                  <div className="tw-relative tw-w-full">
                    <select
                      className="myselect tw-bg-gray-100 tw-border-transparent tw-cursor-pointer tw-w-full tw-rounded-md tw-border tw-py-2 tw-px-3 tw-pr-8"
                      value={String(selectedDriveIndex)}
                      onChange={(e) => setSelectedDriveIndex(parseInt(e.target.value, 10))}
                      disabled={loading}
                    >
                      {driveAccounts.map((a: any, i: number) => (
                        <option key={a.merchant_id || i} value={String(i)}>
                          {driveAccountLabel(a, i)}
                        </option>
                      ))}
                    </select>
                    <div className="tw-absolute tw-top-2 tw-right-1.5 tw-text-slate-400 tw-text-xl tw-pointer-events-none">
                      <HiChevronUpDown />
                    </div>
                  </div>
                </div>
              )}
              <FilePicker
                viewId="SHEETS"
                hasGoogleDrive={googleDriveLinked}
                fileName={pickedSheet?.name}
                setFileInfo={(name, id, accessToken) => setPickedSheet({ id, name, accessToken })}
                selectedEmail={selectedDriveAccount?.email}
              />
              {pickedSheet && (
                <div className="tw-mt-2">
                  <Button size="sm" onClick={handleSheetsPickerUpload} disabled={loading}>
                    {loading ? 'Uploading…' : `Import from ${pickedSheet.name}`}
                  </Button>
                </div>
              )}
            </Form.Group>

            <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-full tw-my-3">
              <hr className="tw-flex-1" />
              <span className="tw-mx-3 tw-text-slate-500 tw-text-sm">or</span>
              <hr className="tw-flex-1" />
            </div>

            <Form.Group className="tw-mb-3">
              <Form.Label>From public Google Sheet or Drive URL</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="https://docs.google.com/spreadsheets/d/… or https://drive.google.com/file/d/…"
                value={sheetUrl}
                disabled={loading}
                onChange={(e) => setSheetUrl(e.target.value)}
              />
              <Form.Text className="tw-text-slate-500">
                The file must be published to the web or shared as "Anyone with the link can view".
              </Form.Text>
              {sheetUrl.trim() && (
                <div className="tw-mt-2">
                  <Button size="sm" onClick={handleSheetsUrlUpload} disabled={loading}>
                    {loading ? 'Uploading…' : 'Import from URL'}
                  </Button>
                </div>
              )}
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {(loading || startingBackfill) && <span className="fa fa-spinner fa-pulse" />}
        {result ? (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={startingBackfill}>
              Cancel
            </Button>
            <Button variant="primary" onClick={startBackfill} disabled={startingBackfill}>
              {startingBackfill ? 'Starting…' : 'Start Backfill'}
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
